import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Trophy, X, Heart, User, Sparkles, MessageCircle, UserPlus, Dices, Loader2, CheckCircle, AlertTriangle, Eye, EyeOff, ShieldAlert } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import VerifiedBadge from './VerifiedBadge';
import { useAuthStore } from '../store/useAuthStore';
import { useChatStore } from '../store/useChatStore';
import { useStoryStore } from '../store/useStoryStore';
import { axiosInstance } from '../lib/axios';
import toast from 'react-hot-toast';

const REPORT_REASONS = [
  "Harassment or Bullying",
  "Inappropriate or NSFW Content",
  "Fake Profile / Impersonation",
  "Spam or Scam",
  "Other Safety Concern",
];

const PublicProfileModal = ({ user, onClose }) => {
  const navigate = useNavigate();
  const { authUser } = useAuthStore();
  const { setSelectedUser } = useChatStore();

  const [isFriend, setIsFriend] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [checkingFriendship, setCheckingFriendship] = useState(true);

  // Casino History & Privacy State
  const [casinoHistory, setCasinoHistory] = useState([]);
  const [loadingCasino, setLoadingCasino] = useState(true);
  const [casinoPrivate, setCasinoPrivate] = useState(false);
  const [isSelf, setIsSelf] = useState(false);
  const [showCasinoHistory, setShowCasinoHistory] = useState(true);
  const [isTogglingPrivacy, setIsTogglingPrivacy] = useState(false);

  // Report Modal State
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState(REPORT_REASONS[0]);
  const [reportDesc, setReportDesc] = useState("");
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);

  // ESC key closes modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        if (showReportModal) {
          setShowReportModal(false);
        } else {
          onClose();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, showReportModal]);

  useEffect(() => {
    const targetId = user?._id || user?.id || user?.userId;
    if (!targetId) return;

    setLoadingCasino(true);
    axiosInstance.get(`/casino/history/user/${targetId}`)
      .then(res => {
        setCasinoHistory(res.data.history || []);
        setCasinoPrivate(Boolean(res.data.isPrivate));
        setIsSelf(Boolean(res.data.isSelf));
        setShowCasinoHistory(res.data.showCasinoHistory !== false);
      })
      .catch(() => {
        setCasinoHistory([]);
      })
      .finally(() => setLoadingCasino(false));
  }, [user?._id, user?.id]);

  useEffect(() => {
    if (!user || !authUser) {
      setCheckingFriendship(false);
      return;
    }

    const targetId = String(user._id || user.id || user.userId || "").toLowerCase();
    const myId = String(authUser._id || authUser.id || authUser.userId || "").toLowerCase();

    if (!targetId || targetId === myId) {
      setIsSelf(true);
      setCheckingFriendship(false);
      return;
    }

    setCheckingFriendship(true);
    setRequestSent(false);
    setIsFriend(false);

    Promise.all([
      axiosInstance.get('/messages/users').catch(() => ({ data: [] })),
      axiosInstance.get('/dating/matches').catch(() => ({ data: [] })),
      axiosInstance.get('/dating/friend-requests').catch(() => ({ data: { incoming: [], outgoing: [] } }))
    ]).then(([sidebarRes, matchesRes, requestsRes]) => {
      const friendsList = [
        ...(Array.isArray(sidebarRes.data) ? sidebarRes.data : []),
        ...(Array.isArray(matchesRes.data) ? matchesRes.data : [])
      ];

      const isAlreadyFriend = friendsList.some(
        u => String(u._id || u.id || "").toLowerCase() === targetId
      );
      setIsFriend(isAlreadyFriend);

      const outgoing = requestsRes.data?.outgoing || [];
      const hasSentRequest = outgoing.some(
        u => String(u._id || u.id || "").toLowerCase() === targetId
      );
      setRequestSent(hasSentRequest);
    }).catch(err => {
      console.error("Error checking friendship status:", err);
    }).finally(() => {
      setCheckingFriendship(false);
    });
  }, [user?._id, user?.id, authUser?._id, authUser?.id]);

  if (!user) return null;

  const handleToggleCasinoPrivacy = async () => {
    setIsTogglingPrivacy(true);
    try {
      const nextVal = !showCasinoHistory;
      await axiosInstance.put('/casino/privacy', { showCasinoHistory: nextVal });
      setShowCasinoHistory(nextVal);
      toast.success(nextVal ? "Casino history is now visible to others 👁️" : "Casino history hidden from public 🔒");
    } catch (err) {
      toast.error("Failed to update casino privacy.");
    } finally {
      setIsTogglingPrivacy(false);
    }
  };

  const handleMessage = () => {
    setSelectedUser(user);
    onClose();
    try {
      useStoryStore.getState().closeViewer();
    } catch (e) {
      // Non-blocking
    }
    navigate('/');
  };

  const handleSendRequest = async () => {
    setIsSending(true);
    try {
      const targetId = user._id || user.id || user.userId;
      await axiosInstance.post(`/dating/like/${targetId}`);
      setRequestSent(true);
      toast.success(`Friend request sent to ${user.fullName}!`);
    } catch (err) {
      const msg = err?.response?.data?.message || 'Could not send request.';
      toast.error(msg);
    } finally {
      setIsSending(false);
    }
  };

  const handleReportUser = async (e) => {
    e.preventDefault();
    const targetId = user?._id || user?.id || user?.userId;
    if (!reportReason) return toast.error("Please select a reason");
    if (!targetId) return toast.error("Invalid user");

    setIsSubmittingReport(true);
    try {
      await axiosInstance.post(`/dating/report/${targetId}`, {
        reason: reportReason,
        description: reportDesc,
      });
      toast.success("Report submitted. Our safety team will review this.");
      setShowReportModal(false);
      setReportReason(REPORT_REASONS[0]);
      setReportDesc("");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to submit report");
    } finally {
      setIsSubmittingReport(false);
    }
  };

  // Render modal to document.body via Portal to prevent clipping from parent transforms or scroll containers
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-5 bg-black/75 backdrop-blur-md animate-fadeIn"
      onClick={onClose}
    >
      <div
        className="bg-base-100 rounded-3xl w-full max-w-md overflow-hidden shadow-2xl border border-white/10 relative max-h-[88vh] flex flex-col my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header Action Buttons (Report & Close) */}
        <div className="absolute top-4 right-4 flex items-center gap-1.5 z-30">
          {!isSelf && (
            <button
              onClick={() => setShowReportModal(true)}
              className="btn btn-circle btn-sm btn-ghost bg-base-200/90 hover:bg-error/20 hover:text-error text-base-content/70 shadow-sm"
              title="Report User"
            >
              <AlertTriangle className="size-4" />
            </button>
          )}
          <button 
            onClick={onClose} 
            className="btn btn-circle btn-sm btn-ghost bg-base-200/90 hover:bg-base-300 text-base-content shadow-sm"
            title="Close"
          >
            <X className="size-4" />
          </button>
        </div>

        {/* Top Header Banner Card */}
        <div className="p-5 pb-4 bg-gradient-to-b from-primary/15 via-base-200/60 to-base-100 shrink-0 border-b border-white/5">
          <div className="flex items-center gap-3.5">
            <div className="relative shrink-0">
              <img 
                src={user.profilePic || "/avatar.png"} 
                alt={user.fullName}
                className="size-18 sm:size-20 rounded-2xl object-cover ring-4 ring-primary/20 shadow-lg" 
              />
              {user.status === 'coupled' && (
                <div className="absolute -bottom-1 -right-1 bg-pink-500 rounded-full p-1 ring-2 ring-base-100" title="Coupled">
                  <Heart className="size-3 text-white fill-current" />
                </div>
              )}
              {isFriend && (
                <div className="absolute -top-1 -right-1 bg-green-500 rounded-full p-1 ring-2 ring-base-100" title="Your Friend">
                  <CheckCircle className="size-3 text-white" />
                </div>
              )}
            </div>

            <div className="flex-1 min-w-0 pr-14">
              <h2 className="text-lg sm:text-xl font-black truncate flex items-center gap-1.5">
                <span>{user.fullName}</span>
                <VerifiedBadge user={user} />
              </h2>
              <p className="text-xs sm:text-sm font-bold text-primary truncate">
                @{user.username || user.email?.split('@')[0]}
              </p>
              
              <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                {isFriend && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-500/10 text-green-600 font-bold text-[10px] rounded-full border border-green-500/20">
                    ✅ Friends
                  </span>
                )}
                
                <div className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-primary/10 text-primary font-black text-[11px] rounded-full border border-primary/20">
                  <Trophy className="size-3" />
                  <span>{user.points || 0} CS</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scrollable Details Section */}
        <div className="p-5 space-y-3.5 overflow-y-auto flex-1">
          
          {/* Bio */}
          <div className="space-y-1">
            <span className="text-[11px] font-bold uppercase tracking-wider text-base-content/60 flex items-center gap-1">
              <User className="size-3" /> Bio
            </span>
            <p className="text-xs sm:text-sm bg-base-200/60 p-3 rounded-2xl border border-white/5 leading-relaxed text-base-content/90">
              {user.bio || "No bio added yet."}
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-2.5">
            <div className="bg-base-200/60 p-3 rounded-2xl border border-white/5 flex flex-col">
              <span className="text-[10px] font-bold uppercase tracking-wider text-base-content/60 flex items-center gap-1 mb-0.5">
                <Dices className="size-3 text-primary" /> Casino Balance
              </span>
              <span className="font-black text-sm truncate text-amber-500">
                {user.points || 0} Coins
              </span>
              <span className="text-[10px] text-base-content/50">
                High-roller status
              </span>
            </div>

            <div className="bg-base-200/60 p-3 rounded-2xl border border-white/5 flex flex-col">
              <span className="text-[10px] font-bold uppercase tracking-wider text-base-content/60 flex items-center gap-1 mb-0.5">
                <Heart className="size-3 text-secondary" /> Relationship
              </span>
              <span className="font-black text-sm capitalize truncate">
                {user.status === 'coupled' ? 'Coupled ❤️' : 'Single / Exploring'}
              </span>
              <span className="text-[10px] text-base-content/50">
                {user.status === 'coupled' ? 'In a paired space' : 'Open to connect'}
              </span>
            </div>
          </div>

          {/* Recent Casino History & Activity */}
          <div className="bg-base-200/60 p-3.5 rounded-2xl border border-white/5 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold uppercase tracking-wider text-base-content/60 flex items-center gap-1.5">
                <Dices className="size-3.5 text-primary" /> Casino History & Play
              </span>

              {/* Privacy Toggle (Only shown to profile owner) */}
              {isSelf && (
                <button
                  onClick={handleToggleCasinoPrivacy}
                  disabled={isTogglingPrivacy}
                  className="flex items-center gap-1 text-[10px] font-black px-2 py-0.5 rounded-lg border border-white/10 hover:bg-base-300 transition-all text-base-content/80"
                  title="Click to toggle visibility"
                >
                  {showCasinoHistory ? (
                    <>
                      <Eye className="size-3 text-emerald-400" />
                      <span>Public (Visible)</span>
                    </>
                  ) : (
                    <>
                      <EyeOff className="size-3 text-amber-400" />
                      <span>Hidden (Private)</span>
                    </>
                  )}
                </button>
              )}
            </div>

            {loadingCasino ? (
              <div className="flex justify-center py-4">
                <Loader2 className="size-4 animate-spin text-primary" />
              </div>
            ) : casinoPrivate ? (
              <div className="p-3.5 bg-base-100/90 rounded-2xl text-center border border-white/5">
                <p className="text-xs font-bold text-base-content/60 flex items-center justify-center gap-1.5">
                  <EyeOff className="size-3.5 text-amber-400" />
                  <span>Casino history is hidden</span>
                </p>
                <p className="text-[10px] text-base-content/40 mt-1">This user has set their casino bets and win history to private.</p>
              </div>
            ) : casinoHistory.length === 0 ? (
              <p className="text-xs text-base-content/50 py-3 text-center">No casino bets placed yet.</p>
            ) : (
              <div className="max-h-36 overflow-y-auto space-y-1.5 pr-1">
                {casinoHistory.map((item) => {
                  const isWin = item.outcome === "win";
                  const isPush = item.outcome === "push";

                  return (
                    <div key={item.id} className="flex items-center justify-between p-2 bg-base-100/80 rounded-xl text-xs border border-white/5">
                      <div className="flex items-center gap-2">
                        <span className="font-black uppercase text-[10px] px-2 py-0.5 rounded-md bg-primary/10 text-primary">
                          {item.gameType}
                        </span>
                        <span className="text-[10px] text-base-content/60 font-semibold">
                          Bet: {item.betAmount} CS
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className={`font-black text-[11px] ${isWin ? "text-emerald-500" : isPush ? "text-amber-500" : "text-rose-500"}`}>
                          {isWin ? `+${item.payoutAmount} CS` : isPush ? `+0 CS` : `-${item.betAmount} CS`}
                        </span>
                        <span className={`badge badge-xs font-bold text-[9px] ${
                          isWin ? "badge-success text-white" : isPush ? "badge-warning text-white" : "badge-error text-white"
                        }`}>
                          {item.outcome?.toUpperCase() || "PLAYED"}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          {!isSelf && (
            <div className="pt-1 flex gap-3">
              {checkingFriendship ? (
                <div className="btn btn-primary flex-1 rounded-2xl" disabled>
                  <Loader2 className="size-4 animate-spin" />
                </div>
              ) : isFriend ? (
                <button
                  onClick={handleMessage}
                  className="btn btn-primary flex-1 rounded-2xl shadow-lg gap-2 text-white font-black text-xs"
                >
                  <MessageCircle className="size-4" /> Message
                </button>
              ) : requestSent ? (
                <button disabled className="btn btn-success flex-1 rounded-2xl gap-2 font-bold text-xs">
                  <CheckCircle className="size-4" /> Request Sent!
                </button>
              ) : (
                <button
                  onClick={handleSendRequest}
                  disabled={isSending}
                  className="btn btn-primary flex-1 rounded-2xl shadow-lg gap-2 text-white font-black text-xs"
                >
                  {isSending ? <Loader2 className="size-4 animate-spin" /> : <UserPlus className="size-4" />}
                  {isSending ? 'Sending...' : 'Send Friend Request'}
                </button>
              )}
            </div>
          )}

          {!isFriend && !checkingFriendship && !isSelf && (
            <p className="text-center text-[11px] text-base-content/40 -mt-1 pb-1">
              💬 You can message each other once they accept your request
            </p>
          )}

        </div>
      </div>

      {/* Report User Modal Popup */}
      {showReportModal && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/85 backdrop-blur-md animate-fadeIn"
          onClick={() => setShowReportModal(false)}
        >
          <div
            className="bg-base-100 rounded-3xl p-5 border border-white/10 shadow-2xl max-w-sm w-full space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between pb-2 border-b border-white/10">
              <div className="flex items-center gap-2 text-error font-black text-sm">
                <ShieldAlert className="size-4" />
                <span>Report User</span>
              </div>
              <button
                onClick={() => setShowReportModal(false)}
                className="btn btn-ghost btn-circle btn-xs"
              >
                <X className="size-3.5" />
              </button>
            </div>

            <form onSubmit={handleReportUser} className="space-y-3">
              <div className="space-y-1">
                <label className="text-xs font-bold text-base-content/70">Reason for report</label>
                <select
                  value={reportReason}
                  onChange={(e) => setReportReason(e.target.value)}
                  className="select select-sm select-bordered w-full rounded-xl text-xs font-semibold"
                >
                  {REPORT_REASONS.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-base-content/70">Details (optional)</label>
                <textarea
                  value={reportDesc}
                  onChange={(e) => setReportDesc(e.target.value)}
                  placeholder="Provide context for our moderators..."
                  rows={3}
                  className="textarea textarea-bordered textarea-sm w-full rounded-xl text-xs"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setShowReportModal(false)}
                  className="btn btn-ghost btn-sm text-xs rounded-xl"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingReport}
                  className="btn btn-error btn-sm text-white text-xs font-black rounded-xl"
                >
                  {isSubmittingReport ? "Submitting..." : "Submit Report"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

export default PublicProfileModal;
